@if (isGeneratingTimesheet()) {
  <div
    class="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-light bg-opacity-75"
    style="z-index: 1050"
  >
    <div class="spinner-border text-primary mb-3" role="status"></div>
    <div
      class="fw-semibold"
      i18n="
        status message|Loading message displayed while generating the timesheet@@generatingTimesheet"
    >
      Generating timesheet…
    </div>
  </div>
}

<!--#region Modals -->

<abc-confirm-modal
  modalId="confirmGenerateTimeSheetModal"
  modalTitle="Auto-fill your timesheet"
  i18n-modalTitle="
    modal title|Title for the confirmation modal to (re)generate the
    timesheet@@confirmGenerateTimesheetTitle"
  [bodyTpl]="confirmGenerateBody"
  (confirmClicked)="generateTimeSheet({ primaryProject: selectedPrimaryProject() })"
  [confirmDisabled]="!canConfirmGeneration()"
/>
<ng-template #confirmGenerateBody>
  <p
    class="text-muted"
    i18n="
      modal body|Body text explaining the consequences of regenerating the timesheet in the
      confirmation modal@@confirmGenerateTimesheetBody"
  >
    Are you sure you want to auto-fill your timesheet? This action will overwrite the current
    registered hours for this month.
  </p>

  @if (!isFreelancer()) {
    <div
      class="fw-semibold mb-1"
      i18n="
        label|Label for the section displaying current absence days in the modal@@CurrentAbsenceDays"
    >
      Current absence days
    </div>
    <div
      class="text-muted mb-1"
      i18n="
        instruction|Instruction text guiding users to verify absence days and link to the absence
        app@@absenceVerificationInstruction"
    >
      Please make sure these are correct before proceeding. If not, go to the
      <a href="https://verlofaanvragen.abc-groep.be/" target="_blank">Absence app</a>
    </div>

    <table class="table table-sm table-striped mb-0">
      <thead>
        <tr>
          <th
            scope="col"
            i18n="column header|Column header for the date in the absence days table@@colDate"
          >
            Date
          </th>
          <th
            scope="col"
            i18n="
              column header|Column header for the absence type in the absence days table@@colType"
          >
            Type
          </th>
          <th
            scope="col"
            i18n="
              column header|Column header for the duration in the absence days table@@colDuration"
          >
            Duration
          </th>
        </tr>
      </thead>
      <tbody>
        @if (!absencesDaysService.isLoading()) {
          @for (abs of absenceDays(); track $index) {
            <tr>
              <td>{{ abs.date | date: 'EEE MMM dd, y' }}</td>
              <td>{{ abs.type }}</td>
              <td>{{ abs.duration }}</td>
            </tr>
          }
        } @else {
          <span
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
        }
      </tbody>
    </table>

    <button
      class="btn btn-sm btn-secondary mt-2"
      (click)="getAbsences()"
      [disabled]="absencesDaysService.isLoading()"
    >
      <i class="bi bi-arrow-clockwise"></i>
      <span
        class="ms-1"
        i18n="button|Button label for refreshing the absence days list@@refreshAbsences"
      >
        Refresh
      </span>
    </button>
  }

  @if (hasMultipleOverlappingProductiveTasks()) {
    <div class="mt-3 pt-2 d-flex flex-column gap-2">
      <label
        for="primaryProjectSelect"
        class="form-label form-label-sm fw-semibold mb-1"
        i18n="label|Label for primary project selector@@primaryProjectLabel"
      >
        Primary project
      </label>
      <div
        class="text-muted mb-1"
        i18n="
          instruction|Instruction text explaining users to select primary project for generation in
          generation modal@@PrimaryProjectSelectionInstruction"
      >
        This is the project that will be auto-filled
      </div>
      <select
        id="primaryProjectSelect"
        class="form-select form-select-sm"
        [ngModel]="selectedPrimaryProject()"
        (ngModelChange)="selectedPrimaryProject.set($event)"
        [class.border-danger]="!selectedPrimaryProject()"
      >
        <option
          [ngValue]="null"
          i18n="option|Default option for project selection@@selectProjectOption"
        >
          Select a project
        </option>
        @for (project of multipleProductiveTasks(); track project.id) {
          <option [ngValue]="project.id">
            {{ project.customerName }} - {{ project.taskName }}
          </option>
        }
      </select>
      @if (!selectedPrimaryProject()) {
        <div
          class="text-danger small"
          i18n="helper|Helper text indicating primary project is required@@primaryProjectRequired"
        >
          Required
        </div>
      }
    </div>
  }
</ng-template>

<abc-confirm-modal
  modalId="confirmCompleteTimesheetModal"
  i18n-modalTitle="
    modal title|Title for the confirmation modal to complete the
    timesheet@@confirmCompleteTimesheetTitle"
  modalTitle="Complete your timesheet"
  [bodyTpl]="confirmCompleteBody"
  (confirmClicked)="completeTimesheet()"
/>

<ng-template #confirmCompleteBody>
  @if (isEarlyCurrentMonth()) {
    <div
      class="alert alert-warning"
      role="alert"
      i18n="warning|Warning message when completing the current month early@@currentMonthWarning"
    >
      Warning: You are about to complete the timesheet for this month before the end of the month.
    </div>
  }
  @if (isFutureMonth()) {
    <div
      class="alert alert-danger"
      role="alert"
      i18n="warning|Warning message when completing a future month timesheet@@futureMonthWarning"
    >
      Warning: You are about to complete a timesheet for a future month.
    </div>
  }
  <p
    class="text-muted mb-2"
    i18n="
      modal body|Body text warning about irreversibility of completing the timesheet in the
      confirmation modal@@confirmCompleteTimesheetBody"
  >
    Are you sure you want to complete your timesheet? You will not be able to change the timesheet
    for this month after this.
  </p>

  <div
    class="fw-semibold mb-1"
    i18n="
      caption|Caption for the total hours summary section in the complete timesheet
      modal@@monthlyTotalsCaption"
  >
    Current total hours this month
  </div>

  <div class="">
    <span
      i18n="label|Label for productive hours in the monthly totals display@@productiveHoursLabel"
      >Productive:</span
    >
    {{ getMonthTotal().productive }}
  </div>
  <div class="">
    <span
      i18n="
        label|Label for non-productive hours in the monthly totals display@@nonProductiveHoursLabel"
      >Non-Productive:</span
    >
    {{ getMonthTotal().nonProductive }}
  </div>

  <div
    class="mb-1 text-muted"
    i18n="
      instruction|Instruction text to confirm accuracy of totals before completing the
      timesheet@@totalsVerificationInstruction"
  >
    Please make sure these are correct before proceeding.
  </div>
</ng-template>

<!--#endregion -->
<div class="d-flex flex-column flex-md-row mb-2">
  @if (pendingChanges() && !allTrainingsHaveDescriptions()) {
    <span class="badge bg-warning text-dark d-flex align-items-center gap-1 mx-md-2">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span
        i18n="
          validation message|Warning message displayed when training entries lack required
          descriptions@@trainingDescriptionRequired"
      >
        All training entries must have a description before saving
      </span>
    </span>
  }

  <div class="d-flex gap-1" role="group">
    <!-- NgbDropdown Container -->
    <div ngbDropdown [placement]="'bottom-end'" class="btn-group position-relative">
      <!-- Main "Auto-fill" button -->
      <button
        class="btn btn-sm btn-secondary d-flex gap-2 align-items-center"
        type="button"
        [disabled]="pendingChanges() || !isGenerationPossible()"
        data-bs-toggle="modal"
        data-bs-target="#confirmGenerateTimeSheetModal"
        (click)="getAbsences(); selectedPrimaryProject.set(null)"
      >
        <i class="bi bi-calendar-check"></i>
        <span
          i18n="
            button|Button to trigger generation of the timesheet with
            confirmation@@autofillTimesheetButton"
        >
          Auto-fill
        </span>
      </button>

      @if (!isFreelancer()) {
        <!-- Split dropdown toggle -->
        <button
          type="button"
          class="btn btn-sm btn-secondary dropdown-toggle dropdown-toggle-split"
          [disabled]="pendingChanges() || !isGenerationPossible()"
          ngbDropdownToggle
        >
          <span class="visually-hidden">Toggle Dropdown</span>
        </button>

        <!-- Dropdown menu -->
        <div
          ngbDropdownMenu
          class="dropdown-menu dropdown-menu-sm-start"
          style="top: 100%; min-width: 100%"
        >
          <button
            ngbDropdownItem
            class="dropdown-item d-flex gap-2 align-items-center"
            type="button"
            [disabled]="pendingChanges()"
            (click)="syncAbsences()"
          >
            <i class="bi bi-arrow-repeat"></i>
            <span i18n="button|Button to regenerate timesheet Absences@@onlyAbsencesSyncButton">
              Sync Absences
            </span>
          </button>
        </div>
      }
    </div>

    <button
      class="btn btn-sm btn-primary d-flex gap-2"
      type="button"
      (click)="save.emit()"
      [disabled]="!canSave()"
    >
      <i class="bi bi-floppy"></i>
      <span i18n="button|Button to save any pending changes to the timesheet@@saveTimesheetButton">
        Save
      </span>
    </button>

    <button
      class="btn btn-sm btn-success d-flex gap-2"
      type="button"
      [disabled]="pendingChanges() || isEveryTaskCompleted() || isCompletingTimesheet()"
      data-bs-toggle="modal"
      data-bs-target="#confirmCompleteTimesheetModal"
    >
      @if (isEveryTaskCompleted()) {
        <i class="bi bi-lock-fill"></i>
      } @else {
        <i class="bi bi-lock"></i>
      }

      @if (isCompletingTimesheet()) {
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      }
      <span
        i18n="
          button|Button to complete the timesheet for the month with
          confirmation@@completeMonthButton"
      >
        Complete
      </span>
    </button>
  </div>
</div>
